import { orgSelectors } from 'ducks/orgs'
import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import { SellerIntegrationType } from 'opensolar-common'
import { Box, ChevronRightOutlineIcon, Grid, IconButton, Skeleton, styled } from 'opensolar-ui'
import { getSupplierConfigNonReact } from 'pages/ordering/hooks/useGetSupplierConfig'
import { useRef } from 'react'
import { useSelector } from 'react-redux'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { DistributorDataTypeV2 } from '../../../elements/hardwareSelectorV2/types'
import { HardwareSupplierFilterKeyType } from '../type'
import { sortByPriceOrderingV2 } from '../utils'

const useStyles = makeOpenSolarStyles((theme: any) => ({
  chip: {
    border: `1px solid rgb(0, 0, 0, 0.3)`,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
  },
  selectedChip: {
    border: `2px solid ${theme.blue4}`,
  },
}))

const ChipImage = styled('div')({
  height: 20,
  width: 36,
  padding: 4,
})

const ChipList = styled('div')(({ isScrollable }: { isScrollable: boolean }) => ({
  display: 'flex',
  gap: 4,
  flexWrap: isScrollable ? 'nowrap' : 'wrap',
  overflowX: 'auto',
  width: isScrollable ? '198px' : 'auto',
  scrollbarWidth: 'none',
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 20,
  height: 20,
  color: theme.palette.grey[900],
  aspectRatio: '1/1',
  '& svg path': {
    strokeWidth: 1,
  },
}))

const IconButtonWrapper = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const getDistributorImagePath = (distributor: HardwareSupplierFilterKeyType, config: SellerIntegrationType) => {
  // New distributors that have their config coming from API
  if (config?.logoUrl) {
    return config.logoUrl
  }
  // TODO verify if this ever gets called.
  return getSupplierConfigNonReact(distributor)?.logoUrl
}

interface DistributorChipsProps {
  ordering_v2: DistributorDataTypeV2[]
  onSelect: (event, distributor: DistributorDataTypeV2) => void
  selectedDistributor: DistributorDataTypeV2 | null | undefined
  location?: 'product-tile' | 'product-details'
}

const DistributorChips = ({
  ordering_v2,
  onSelect,
  selectedDistributor,
  location = 'product-tile',
}: DistributorChipsProps) => {
  const classes = useStyles()
  const distributorConfigs = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)

  const chipListRef = useRef<HTMLDivElement>(null)

  const distributors = sortByPriceOrderingV2(ordering_v2)
  const hasMultipleDistributors = distributors.length > 1

  const handleClick = (event, distributor) => {
    event?.stopPropagation()
    onSelect(event, distributor)
  }

  const handleScroll = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const container = chipListRef.current
    if (!container) return
    const scrollAmount = container.clientWidth
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    })
  }

  const isProductTile = location === 'product-tile'

  return (
    <Grid container alignContent="center">
      <ChipList ref={chipListRef} isScrollable={isProductTile}>
        {distributors.map((distributorData) => {
          const config = distributorConfigs.find((config) => config.filterKey === distributorData.distributor)
          if (!config) {
            return null
          }

          return (
            <Grid item key={config.filterKey}>
              <Box
                onClick={(event) => handleClick(event, distributorData)}
                className={`${classes.chip} ${
                  selectedDistributor?.distributor === distributorData.distributor && hasMultipleDistributors
                    ? classes.selectedChip
                    : ''
                }`}
              >
                <ChipImage>
                  <SupplierLogoWithFallback
                    logoUrl={getDistributorImagePath(distributorData.distributor, config)}
                    name={config.name}
                  />
                </ChipImage>
              </Box>
            </Grid>
          )
        })}
      </ChipList>

      {isProductTile && distributors.length > 4 && (
        <IconButtonWrapper item>
          <StyledIconButton onClick={handleScroll}>
            <ChevronRightOutlineIcon />
          </StyledIconButton>
        </IconButtonWrapper>
      )}
    </Grid>
  )
}

export const DistributorChipsLoading = () => {
  return (
    <Grid container spacing={1}>
      <Grid item>
        <Skeleton width={37} height={26} />
      </Grid>
    </Grid>
  )
}

export default DistributorChips
